/* eslint-disable react/no-unused-prop-types */
import { faTimesCircle, faChevronRight } from "@fortawesome/pro-light-svg-icons";
import { faStar, faFilter } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import exact from "prop-types-exact";
import { createRef, Component } from "react";
import classNames from "classnames";
import { debounce, flatten, isEmpty, orderBy } from "lodash-es";
import { connect } from "react-redux";
import { toast } from "extensions/toast";
import PropTypes from "prop-types";
import { v4 as uuid } from "uuid";

import { store } from "common/store";
import { Tooltip } from "common/components/tooltip";

import { GET_FILTERS, SAVE_NEW_FILTER, UPDATE_FILTER, DELETE_SAVED_FILTER } from "common/actions/filterBar";
import { UPDATE_USER_MERCHANT_SETTINGS } from "common/actions/user_merchant_settings";
import { SET_PAGE_QUALIFIER_TYPE } from "common/actions/qualifiers";

import { Qualifiers } from "../queryBuilder/qualifiers";

import { TypeAheadInput } from "./TypeAhead";
import { TypeAheadResults } from "./TypeAheadResults";
import { AllFilters } from "./AllFilters";
import { SavedFilters } from "./savedFilters";
import { buildSourceData } from "./BuildSrcData";
import { FilterBarMenu } from "./FilterBarMenu";
import { FilterToken } from "./FilterToken";
import { FilterBarCategories } from "./FilterBarCategories";
import { getSelectedCategoriesAndOperators } from "./FilterBarUtils";
import { TextModeInput } from "./FIlterBarTextModeInput";
import { FiltersModal } from "./FiltersModal";
import { TokenField } from "./TokenField";

import "./NewFilterBar.scss";

const mapStateToProps = (state) => ({
    filtersLoaded: state.filters.loaded,
    filters: state.filters.filters,
    mode: state.user_merchant_settings["searchbar:mode"]
});

const mapDispatchToProps = {
    DELETE_SAVED_FILTER,
    GET_FILTERS,
    SAVE_NEW_FILTER,
    SET_PAGE_QUALIFIER_TYPE,
    UPDATE_FILTER,
    UPDATE_USER_MERCHANT_SETTINGS
};

const FilterBar = class extends Component {
    static defaultProps = {
        mode: "token",
        qualifiers: "",
        allowExclude: true,
        onePerCategory: false,
        onChange: () => {},
        sources: "*",
        readOnlyDimensions: new Set(),
        filtersLoaded: false,
        filters: [],
        categoryModeAvailable: true,
        overrideMode: "replace",
        sourcesOverride: null,
        className: "",
        aquirerHasPrecedence: false,
        isBenchmark: false,
        container: "document",
        alwaysOpen: false,
        focusOnMount: true
    };
    
    static propTypes = exact({
        className: PropTypes.string,
        qualifiers: PropTypes.string,
        mode: PropTypes.string,
        allowExclude: PropTypes.bool,
        onePerCategory: PropTypes.bool,
        onChange: PropTypes.func,
        sources: PropTypes.oneOfType([
            PropTypes.oneOf(["*"]),
            PropTypes.array
        ]),
        overrideMode: PropTypes.string,
        sourcesOverride: (props, propName, componentName) => this.validateSourcesOverride,
        readOnlyDimensions: PropTypes.objectOf(PropTypes.shape()),
        filtersLoaded: PropTypes.bool,
        filters: PropTypes.arrayOf(PropTypes.exact({
            _id: PropTypes.string,
            active: PropTypes.bool,
            created: PropTypes.string,
            merchant_ref: PropTypes.string,
            modified: PropTypes.string,
            name: PropTypes.string,
            qualifiers: PropTypes.string,
            tags: PropTypes.arrayOf(PropTypes.string),
            user_ref: PropTypes.string
        })),
        categoryModeAvailable: PropTypes.bool,
        GET_FILTERS: PropTypes.func.isRequired,
        DELETE_SAVED_FILTER: PropTypes.func.isRequired,
        SAVE_NEW_FILTER: PropTypes.func.isRequired,
        UPDATE_FILTER: PropTypes.func.isRequired,
        UPDATE_USER_MERCHANT_SETTINGS: PropTypes.func.isRequired,
        SET_PAGE_QUALIFIER_TYPE: PropTypes.func.isRequired,
        aquirerHasPrecedence: PropTypes.bool,
        isBenchmark: PropTypes.bool,
        container: PropTypes.string,
        alwaysOpen: PropTypes.bool,
        focusOnMount: PropTypes.bool
    });
    
    constructor(props) {
        super(props);
        this.filterBar = createRef();
        this.typeAhead = createRef();
        this.allData = [];
        
        // if category mode is off by props can't load it into that mode from local storage.
        const getMode = () => {
            const mode = props.mode;
            if (!props.categoryModeAvailable && mode === "categories") {
                return "token";
            }
            return mode;
        };
        
        this.state = {
            dataLoaded: false,
            open: props.alwaysOpen,
            menuOpen: false,
            modalOpen: false,
            modalAction: "new",
            mode: getMode(),
            filterPaneMode: "search",
            results: {
                value: "",
                raw: [],
                grouped: []
            },
            qualifiers_str: props.qualifiers,
            temp_qualifiers_str: "",
            paneView: "all",
            paneHeight: 800,
            suggestion: { value: "", isDefault: false },
            groupedData: {}
        };
        
        this.debouncedHandleResize = debounce(() => {
            if (!this.props.alwaysOpen) {
                this.setState({ open: false });
            }
        }, 100);
        this.onChangeDebounced = debounce(() => {
            this.props.onChange(this.getQualifiers());
        }, 300);
    }
    
    componentDidMount() {
        window.addEventListener("resize", this.debouncedHandleResize);
        document.addEventListener("click", this.checkIfClickFromOutsideFilterBar);
        
        if (!store.getState().filters.loaded) {
            this.props.GET_FILTERS().catch((error) => toast.error("Unable to load saved filters."));
        }
    
        const srcData = this.getFilterBarData();
        this.allData = flatten(Object.values(srcData));
        this.setState({
            dataLoaded: true,
            groupedData: srcData
        });
    }
    
    componentDidUpdate(prevProps, prevState, snapshot) {
        let resetOverride;
        if (this.props.sourcesOverride === null) {
            resetOverride = false;
        } else if (prevProps.sourcesOverride === null) {
            resetOverride = true;
        } else {
            resetOverride = Object.keys(this.props.sourcesOverride.data).every((key) => Object.keys(prevProps.sourcesOverride.data).includes(key));
        }
        const srcData = this.getFilterBarData();
        const hasSrcData = !(isEmpty(srcData)) && typeof srcData !== "undefined";
        /** @type {keyof QUALIFIER_CATEGORY} */
        let pageQualifierType = "unset";

        if (hasSrcData) {
            if (srcData.acquirer && srcData.acquirer.length && !srcData.provider) {
                pageQualifierType = "acquirer";
            } else if (srcData.provider && srcData.provider.length && !srcData.acquirer) {
                pageQualifierType = "provider";
            }
        }
        this.props.SET_PAGE_QUALIFIER_TYPE(pageQualifierType);
        if (resetOverride
            && hasSrcData
            && typeof prevState.srcData !== "undefined"
            && Object.keys(srcData) !== Object.keys(prevState.srcData)
        ) {
            this.allData = flatten(Object.values(srcData));
            this.setGroupedData(srcData);
        }
        if (prevProps.qualifiers !== this.props.qualifiers && this.state.qualifiers_str !== this.props.qualifiers) {
            this.resetQualifiersFromProps();
            return;
        }
        if (this.state.qualifiers_str !== prevState.qualifiers_str) { // TODO: We need static method to compare qualifiers by their values, not string representations
            this.onChangeDebounced();
        }
        
        if (!prevState.open && this.state.open) {
            document.body.classList.add("noscroll");
        } else if (prevState.open && !this.state.open) {
            document.body.classList.remove("noscroll");
        }
    }
    
    componentWillUnmount() {
        document.removeEventListener("click", this.checkIfClickFromOutsideFilterBar);
        document.removeEventListener("keypress", this.checkKeyUpDown);
        this.onChangeDebounced.cancel();
        this.debouncedHandleResize.cancel();
        window.removeEventListener("resize", this.debouncedHandleResize);
        this.props.SET_PAGE_QUALIFIER_TYPE("unset"); // TODO implement JSDOCs to lint if value is not recognized
    }
    
    setGroupedData = (srcData) => this.setState({ groupedData: srcData });
    
    getResultsPaneHeight() {
        try {
            const filterBarCLientRect = this.filterBar.current?.getBoundingClientRect(); // read client rectangle once, every read forces a reflow.
            const filterBarTop = filterBarCLientRect?.top || 0;
            const filterBarHeight = filterBarCLientRect?.height || 0;
            if (this.props.container === "document") {
                return Math.max(300, document.documentElement.clientHeight - filterBarTop - (filterBarHeight + 4));
            }
            return Math.max(300, this.filterBar.current.closest(this.props.container).clientHeight - (filterBarHeight + 4));
        } catch {
            return 800;
        }
    }
    
    getQualifiers() {
        return new Qualifiers({ value: this.state.qualifiers_str });
    }
    
    getTempQualifiers() {
        return new Qualifiers({ value: this.state.temp_qualifiers_str });
    }
    
    getFilterBarData = () => {
        const srcData = buildSourceData({
            sources: this.props.sources,
            overrideMode: this.props.overrideMode,
            sourcesOverride: this.props.sourcesOverride,
            isBenchmark: this.props.isBenchmark
        });
        
        // Never include both acquirer and provider in the same filter bar.
        // Always pick provider in this case but skip check if custom sources passed in.
        if (srcData.acquirer && srcData.provider) {
            if (this.props.aquirerHasPrecedence) {
                delete srcData.provider;
            } else {
                delete srcData.acquirer;
            }
        }
        return srcData;
    };
    
    validateSourcesOverride = (props, propName, componentName) => {
        const isObjectError = PropTypes.object(props, propName, componentName);
        if (isObjectError) { return isObjectError; }
        
        const baseErrorMsg = `Invalid ${propName} provided to ${componentName}`;
        
        const sourcesOverride = props[propName];
        
        const missingKey = ["data", "meta", "resource"].find((key) => typeof sourcesOverride[key] === "undefined");
        if (missingKey) {
            return new Error(`${baseErrorMsg}, missing ${missingKey} key.`);
        }
        
        const data = sourcesOverride.data;
        const meta = sourcesOverride.meta;
        const resource = sourcesOverride.resource;
        
        const badValue = [data, meta].find((val) => typeof val !== "object");
        if (badValue) {
            return new Error(`${baseErrorMsg}, data and meta keys expect an object.`);
        }
        if (typeof resource !== "string") {
            return new Error(`${baseErrorMsg}, resource key expects a string.`);
        }
        
        const dataKeys = Object.keys(data);
        const metaKeys = Object.keys(meta);
        if (!dataKeys.length === metaKeys.length && dataKeys.every((elem, index) => elem === metaKeys[index])) {
            return new Error(`${baseErrorMsg}, meta and data's values' keys must match.`);
        }
        
        const badDataVal = dataKeys.find((dataKey) => data[dataKey] !== "object");
        
        if (badDataVal) {
            return new Error(`${baseErrorMsg}, data's values' attributes expect an object as the value.`);
        }
        
        const badMetaVal = metaKeys.find((metaKey) => meta[metaKey] !== "object");
        if (badMetaVal) {
            return new Error(`${baseErrorMsg}, meta's values' attributes expect an object as the value.`);
        }
        
        // TODO: these are additional verifications that could be added, but it's already getting a bit cumbersome.
        // check that each of data attributes' values is a string and meta's attributes' values is an object
        // check that each of meta's value's value's values is an object
        // check that the values on that object are strings except for the one list case
        return null;
    };
    
    checkIfClickFromOutsideFilterBar = (e) => {
        const filterBar = e.target.closest(".query-builder2");
        if (!filterBar && !this.state.modalOpen && !this.props.alwaysOpen) {
            this.setState({ open: false });
        }
    };
    
    deleteQualifier = (token, temp) => {
        this.setState((prevState, props) => {
            if (temp) {
                const qualifiers = new Qualifiers({ value: prevState.temp_qualifiers_str });
                qualifiers.remove(token.pivot, { value: token.label });
                return {
                    temp_qualifiers_str: qualifiers.toString()
                };
            }
            const qualifiers = new Qualifiers({ value: prevState.qualifiers_str });
            qualifiers.remove(token.pivot || token.category, { value: token.label });
            return {
                qualifiers_str: qualifiers.toString()
            };
        });
    };
    
    updateFromTokenMenu = (updates) => {
        const tokens = [];
        updates.selectedItems.forEach((i) => {
            tokens.push(this.allData.find((item) => item.id === i));
        });
        const refs = tokens.map((r) => Qualifiers.normalizeValue(r.ref));
        this.setState((prevState, props) => {
            const qualifiers = new Qualifiers({ value: prevState.qualifiers_str });
            if (refs.length) {
                qualifiers.addOrReplace(`${updates.operator}${updates.category}:${refs.join(",")}`);
            } else {
                qualifiers.remove(updates.category);
            }
            
            return {
                qualifiers_str: qualifiers.toString()
            };
        });
    };
    
    getAllSelectedIdsInCategory = (c) => {
        const tokens = this.getQualifiers().toTokens().filter((t) => t.category === c);
        const selectedItems = [];
        const srcPivotData = this.state.groupedData[c] || [];
        
        if (!srcPivotData.length) return [];
        
        tokens.forEach((token) => {
            const t = srcPivotData.find((s) => Qualifiers.normalizeValue(s.ref) === Qualifiers.normalizeValue(token.ref));
            if (t?.id) {
                selectedItems.push(t.id);
            }
        });
        return selectedItems;
    };
    
    getTokens = (category, hideMenu) => {
        if (this.state.dataLoaded) {
            let permTokens;
            let tempTokens;
            const qualifiersTokens = this.getQualifiers().toTokens();
            const temp_qualifiersTokens = this.getTempQualifiers().toTokens();
            let readOnlyTokens = "";
            
            if (category) {
                permTokens = qualifiersTokens.filter((t) => t.category === category && !this.props.readOnlyDimensions.has(category));
                readOnlyTokens = qualifiersTokens.filter((t) => t.category === category && this.props.readOnlyDimensions.has(category));
                tempTokens = temp_qualifiersTokens.filter((t) => t.category === category);
            } else {
                permTokens = qualifiersTokens.filter((t) => !this.props.readOnlyDimensions.has(t.category));
                readOnlyTokens = qualifiersTokens.filter((t) => this.props.readOnlyDimensions.has(t.category));
                tempTokens = temp_qualifiersTokens;
            }
            
            const perm = permTokens.map((token) => {
                const srcPivotData = this.state.groupedData[token.category] || [];
                const srcToken = srcPivotData.find((s) => Qualifiers.normalizeValue(s.ref) === Qualifiers.normalizeValue(token.ref)) || {};
                return (
                    <FilterToken
                        key={`filtertoken_${token.operator}_${srcToken.id}`}
                        onUpdate={this.updateFromTokenMenu}
                        onDelete={this.deleteQualifier}
                        token={{ ...token, ...srcToken }}
                        data={srcPivotData}
                        selectedItems={this.getAllSelectedIdsInCategory(token.category)}
                        hideMenu={hideMenu}
                        temp={false}
                        isReadOnly={false}
                    />
                );
            });
    
            const readOnly = readOnlyTokens.map((token) => {
                const srcPivotData = this.state.groupedData[token.category] || [];
                const srcToken = srcPivotData.find((s) => Qualifiers.normalizeValue(s.ref) === Qualifiers.normalizeValue(token.ref)) || {};
                return (
                    <FilterToken
                        key={`filtertoken_${token.operator}_${srcToken.id}`}
                        token={{ ...token, ...srcToken }}
                        data={srcPivotData}
                        selectedItems={this.getAllSelectedIdsInCategory(token.category)}
                        hideMenu
                        temp={false}
                        isReadOnly
                    />
                );
            });
            
            const temp = tempTokens.map((token) => {
                const srcPivotData = this.state.groupedData[token.category];
                const srcToken = srcPivotData.find((s) => Qualifiers.normalizeValue(s.ref) === Qualifiers.normalizeValue(token.ref));
                if (srcToken) {
                    return (
                        <FilterToken
                            key={`filtertoken_${token.operator}_${srcToken.id}`}
                            onUpdate={this.updateFromTokenMenu}
                            onDelete={this.deleteQualifier}
                            token={{ ...token, ...srcToken }}
                            data={srcPivotData}
                            selectedItems={this.getAllSelectedIdsInCategory(token.category)}
                            temp
                            isReadOnly={false}
                        />
                    );
                }
                return null;
            });
            return orderBy([...perm, ...readOnly, ...temp], ["props.isReadOnly", "props.token.category", "props.token.alias"], ["desc", "asc", "asc"]);
        }
        return null;
    };
    
    clearCategory = (category) => {
        this.setState((prevState, props) => {
            const qualifiers = new Qualifiers({ value: prevState.qualifiers_str });
            qualifiers.remove(category);
        
            return {
                suggestion: { value: "", isDefault: false },
                qualifiers_str: qualifiers.toString(),
                open: false
            };
        });
    };
    
    filterItemClick = (operator, id, isTemp = false) => {
        const srcFilterItem = this.allData.find((item) => item.id === id);
        if (this.typeAhead?.current) {
            this.typeAhead.current.clearInput();
        }
        this.setState((prevState) => {
            const newState = {
                suggestion: { value: "", isDefault: false }
            };
            const qualifiers = new Qualifiers({ value: isTemp ? prevState.temp_qualifiers_str : prevState.qualifiers_str });
            qualifiers.add(`${operator}${srcFilterItem.pivot}:${Qualifiers.normalizeValue(srcFilterItem.ref)}`);
            const qualifiersStr = qualifiers.toString();
            if (isTemp) {
                newState.temp_qualifiers_str = qualifiersStr;
            } else {
                newState.open = this.props.alwaysOpen;
                newState.qualifiers_str = qualifiersStr;
            }
            return newState;
        });
    };
    
    savedFilterClick = (id) => {
        const qualifiers = this.getQualifiers();
        qualifiers.clean();
        const filter = this.props.filters.find((f) => f._id === id);
        qualifiers.add(filter?.qualifiers || "");
        this.applyTextMode(qualifiers);
    };
    
    saveFilter = (filter) => {
        this.props.SAVE_NEW_FILTER(filter).then(() => {
            this.setState({ modalOpen: false });
        }).catch((error) => {
            toast.error("Unexpected error saving filter.  Please try again later");
        });
    };
    
    updateFilter = (filter) => {
        this.props.UPDATE_FILTER(filter).then(() => {
            this.setState({ modalOpen: false });
        }).catch((error) => {
            toast.error("Unexpected error updating filter.  Please try again later");
        });
    };
    
    isSavedFilter = () => this.props.filters.find((f) => f.qualifiers === this.state.qualifiers_str) || false;
    
    filterClick = (id) => () => {
        this.props.savedFilterClick(id);
    };
    
    suggestionChanged = (filterItem, isDefault = true) => {
        this.setState({ suggestion: { value: filterItem?.alias, isDefault } });
    };
    
    getPaneContent = () => {
        if (this.state.filterPaneMode === "typeahead") {
            const suggestions = [-1];
            for (let i = 0; i < Object.keys(this.state.results.grouped).length; i++) {
                suggestions.push(-1);
            }
            
            return (
                <TypeAheadResults
                    open={this.state.open}
                    allowExclude={this.props.allowExclude}
                    height={this.state.paneHeight}
                    results={this.state.results}
                    suggestions={suggestions}
                    onFilterItemClick={this.filterItemClick}
                    suggestionChanged={this.suggestionChanged}
                    key={`typeAheadTabs_${this.state.results.value}`}
                />
            );
        }
        const getView = () => {
            if (this.state.dataLoaded) {
                if (this.state.paneView === "all") {
                    return (
                        <AllFilters
                            onFilterItemClick={this.filterItemClick}
                            allData={this.state.groupedData}
                            height={this.state.paneHeight}
                            open={this.state.open}
                            selectedCategories={getSelectedCategoriesAndOperators(this.getQualifiers())}
                            getAllSelectedIdsInCategory={this.getAllSelectedIdsInCategory}
                            readOnlyDimensions={this.props.readOnlyDimensions}
                        />
                    );
                }
                return (
                    <SavedFilters
                        qualifiers={this.getQualifiers()}
                        deleteFilter={this.props.DELETE_SAVED_FILTER}
                        savedFilterClick={this.savedFilterClick}
                        allData={this.state.groupedData}
                        filters={this.props.filters}
                        filtersLoaded={this.props.filtersLoaded}
                    />
                );
            }
            return null;
        };
        
        return (
            <div className="filters-menu">
                <aside>
                    <div className={classNames("aside-item", { active: this.state.paneView === "all" }, { inactive: this.state.paneView === "saved" })} onClick={() => this.setState({ paneView: "all" })}>All filters <FontAwesomeIcon className="chevron-icon" icon={faChevronRight}/></div>
                    <div className={classNames("aside-item", { active: this.state.paneView === "saved" }, { inactive: this.state.paneView === "all" })} onClick={() => this.setState({ paneView: "saved" })}>Saved <FontAwesomeIcon className="chevron-icon" icon={faChevronRight}/></div>
                    <div className="aside-item">Recent</div>
                    <div className="recent-filters">
                        {this.props.filters.map((f) => (<a key={`filterlink_${f._id}`} onClick={(e) => { e.preventDefault(); this.savedFilterClick(f._id); }} href="#">{f.name}</a>))}
                    </div>
                </aside>
                <div className="view">
                    {getView()}
                </div>
            </div>
        );
    };
    
    clickForInput = (e) => {
        const token = e.target.closest(".token");
        if (!token) {
            this.typeAhead.current.setFocus();
            this.clickFilterIcon();
        }
    };
    
    clickFilterIcon = () => {
        if (this.state.dataLoaded) {
            this.setState((prevState, props) => ({
                filterPaneMode: "filterIcon",
                open: this.props.alwaysOpen || !prevState.open,
                menuOpen: false,
                paneHeight: this.getResultsPaneHeight()
            }));
        }
    };
    
    typeAheadResultsChanged = (results) => {
        const omit = results.isOmit ? "-" : "";
        const suggestion = results?.raw?.length ? `${omit}${results.raw[0].alias}` : "";
        this.setState({
            filterPaneMode: "typeahead",
            paneHeight: this.getResultsPaneHeight(),
            results,
            open: results.value.length,
            suggestion: { value: suggestion, isDefault: true }
        });
    };
    
    addCategory = (category, operator) => {
        this.setState((prevState, props) => {
            const qualifiers = new Qualifiers({ value: prevState.qualifiers_str });
            qualifiers.add(`${operator}${category}:`);
            
            return {
                qualifiers_str: qualifiers.toString()
            };
        });
    };
    
    applyTextMode = (qualifiers) => {
        this.props.UPDATE_USER_MERCHANT_SETTINGS({ "searchbar:mode": "token" });
        this.setState({
            qualifiers_str: qualifiers.toString(),
            mode: "token",
            open: false
        });
    };
    
    deleteLastToken = () => {
        if (this.state.mode === "token") {
            const tokens = this.getTokens();
            if (tokens.length) {
                this.deleteQualifier(tokens[tokens.length - 1].props.token, false);
            }
        }
    };
    
    getTypeAhead = () => {
        if (this.state.dataLoaded) {
            return (
                <TypeAheadInput
                    showSearchIcon={false}
                    ref={this.typeAhead}
                    srcData={this.allData}
                    onTypeAhead={this.typeAheadResultsChanged}
                    selectedCategories={this.getQualifiers().value.map((q) => q.category)}
                    onePerCategory={this.props.onePerCategory}
                    suggestion={this.state.suggestion}
                    readOnlyDimensions={this.props.readOnlyDimensions}
                    onDelete={this.deleteLastToken}
                    focusOnMount={this.props.focusOnMount}
                />
            );
        }
        return null;
    };
    
    renderCurrentMode = () => {
        if (this.state.mode === "token") {
            return (
                <>
                    <div className="filter-icon" onClick={this.clickFilterIcon}>
                        <span className="fa-layers fa-fw" aria-hidden="true">
                            <FontAwesomeIcon className="filter-menu-icon" icon={faFilter}/>
                            <FontAwesomeIcon className="chevron-menu-icon" transform="shrink-10 right-10" icon={faChevronDown}/>
                        </span>
                    </div>
                    <div className="tokens" onClick={this.clickForInput}>
                        <TokenField>
                            <div className="tokenfield ">
                                {this.getTokens()}
                                {this.getTypeAhead()}
                            </div>
                        </TokenField>
                        
                    </div>
                </>
            );
        }
        
        if (this.state.mode === "text") {
            return (
                <div className="text">
                    <div className="form-control">
                        <TextModeInput qualifiers={this.getQualifiers()} applyTextMode={this.applyTextMode}/>
                    </div>
                </div>
            );
        }
        return (
            <>
                <div className="filter-icon" onClick={this.clickFilterIcon}>
                    <span className="fa-layers fa-fw" aria-hidden="true">
                        <FontAwesomeIcon className="filter-menu-icon" icon={faFilter}/>
                        <FontAwesomeIcon className="chevron-menu-icon" transform="shrink-10 right-10" icon={faChevronDown}/>
                    </span>
                </div>
                <FilterBarCategories
                    qualifiers={this.getQualifiers()}
                    tempQualifiers={this.getTempQualifiers()}
                    getTokens={this.getTokens}
                    allData={this.state.groupedData}
                    addCategory={this.addCategory}
                    filterItemClick={this.filterItemClick}
                    readOnlyDimensions={this.props.readOnlyDimensions}
                    onUpdate={this.updateFromTokenMenu}
                    clearCategory={this.clearCategory}
                />
            </>
        );
    };
    
    // FilterBar Menu methods
    changeFilterBarMode = (newMode, open) => {
        this.props.UPDATE_USER_MERCHANT_SETTINGS({ "searchbar:mode": newMode });
        this.setState((prevState, props) => ({
            menuOpen: open || !prevState.menuOpen,
            mode: newMode
        }));
    };
    
    menuClosed = () => {
        this.setState({ menuOpen: false });
    };
    
    menuOpened = () => {
        this.setState({ menuOpen: true, open: false });
    };
    
    star = (e) => {
        e.preventDefault();
        const action = this.isSavedFilter() ? "edit" : "new";
        this.openModal(action);
    };
    
    openModal = (action) => {
        this.setState({
            menuOpen: false, open: false, modalAction: action, modalOpen: true
        });
    };
    
    closeModal = () => {
        this.setState({
            menuOpen: false, open: false, modalOpen: false, modalAction: ""
        });
    };
    
    applyTempTokens = (e) => {
        e.preventDefault();
        this.typeAhead.current.clearInput();
        this.setState((prevState, props) => {
            const qualifiers = new Qualifiers();
            qualifiers.add(prevState.qualifiers_str);
            qualifiers.add(prevState.temp_qualifiers_str);
            return {
                suggestion: { value: "", isDefault: false },
                qualifiers_str: qualifiers.toString(),
                temp_qualifiers_str: "",
                open: false
            };
        });
    };
    
    clearAllTokens = (e) => {
        e.preventDefault();
        
        this.setState({
            qualifiers_str: "",
            temp_qualifiers_str: ""
        });
    };
    
    resetQualifiersFromProps() {
        this.setState({
            qualifiers_str: this.props.qualifiers,
            temp_qualifiers_str: ""
        });
    }
    
    render() {
        const hasTokens = this.state.qualifiers_str !== "";
        const hasTempTokens = this.state.temp_qualifiers_str !== "";
        const hasAnyTokens = hasTempTokens || hasTokens;
        const savedFilter = this.isSavedFilter();
       
        const paneStyle = {
            height: `${this.state.paneHeight}px`
        };
        
        return (
            <div className={classNames("query-builder2", this.props.className, { "applied-saved-filter": savedFilter }, { "filters-open": this.state.open && this.state.panelMode === "filterIcon" })} ref={this.filterBar}>
                <div className={classNames("saved-filter-name", { hidden: !savedFilter })}>{savedFilter?.name}</div>
                <div className={classNames("tokens", { "has-saved-filter-true": savedFilter }, { "has-saved-filter-false": !savedFilter })}>
                    <div className="typeahead-cont">
                        {this.renderCurrentMode()}
                    </div>
                </div>
                <div className="controls">
                    <a href="#" onClick={this.applyTempTokens} className={classNames("text-apply", { hidden: !hasTempTokens })}>Apply</a>
                    <Tooltip title="Clear all filters">
                        <a href="#" onClick={this.clearAllTokens} className={classNames({ hidden: !hasAnyTokens })}>
                            <FontAwesomeIcon icon={faTimesCircle} />
                        </a>
                    </Tooltip>
                    <Tooltip title="Save this filter for future use">
                        <a href="#" onClick={this.star} className={classNames("save-filter", { hidden: hasTempTokens || !hasAnyTokens })}>
                            <FontAwesomeIcon icon={faStar} className={classNames({ "saved-filter-applied": savedFilter })} />
                        </a>
                    </Tooltip>
                    <FilterBarMenu
                        categoryModeOn={this.props.categoryModeAvailable}
                        mode={this.state.mode}
                        changeFilterBarMode={this.changeFilterBarMode}
                        onOpen={this.menuOpened}
                        onClose={this.menuClosed}
                        openModal={this.openModal}
                        open={this.state.menuOpen}
                        savedFilter={savedFilter}
                        qualifiers={this.getQualifiers()}
                    />
                </div>
                <div style={paneStyle} className={classNames("results-pane", { hidden: !this.state.open })}>{this.getPaneContent()}</div>
                <FiltersModal
                    key={uuid()}
                    open={this.state.modalOpen}
                    closeModal={this.closeModal}
                    modalOpen={this.state.modalOpen}
                    qualifiers={this.getQualifiers()}
                    filters={this.props.filters}
                    initialAction={this.state.modalAction}
                    saveFilter={this.saveFilter}
                    isSavedFilter={this.isSavedFilter}
                    updateFilter={this.updateFilter}
                    getTokens={this.getTokens}
                />
            </div>
        );
    }
};

export const FilterBarRedux = connect(mapStateToProps, mapDispatchToProps)(FilterBar);
